import {
  AdvertiserProfile,
  AdvertiserProfileRequest,
  AdvertiserStatsResponse,
  AiFlagResolveRequest,
  BillboardAvailabilityResponse,
  BillboardListResponse,
  CreativeSubmitRequest,
  CreativeVersionView,
  MyAdvertisersResponse,
  MyCreativesResponse,
  PlacementUpdateRequest,
  PlacementUpdateResponse,
  PropertyListResponse,
  type PropertyPublic,
  ReviewDecisionRequest,
  ReviewQueueResponse,
  UploadIntentRequest,
  UploadIntentResponse,
  UploadView,
} from "@townsy/contracts";
import { z } from "zod";
import { API_BASE, ApiError, apiFetch } from "./client";

/**
 * Advertiser operations (M5, ADR-0009): profiles, uploads, creatives, review, placement,
 * billboards and private statistics. Every call goes to the same-origin API with the session
 * cookie; the server authorises, validates and audits. Responses are validated against the shared
 * contracts; where the brief leaves the wrapper open (`{ advertiser }` vs the bare object) both
 * shapes are accepted.
 */

const enc = encodeURIComponent;

/** Accept `{ [key]: T }` or a bare `T` (the route list does not pin the wrapper). */
function wrappedOrBare<T extends z.ZodType>(schema: T, key: string) {
  return z.union([z.object({ [key]: schema }).loose().transform((o) => (o as Record<string, unknown>)[key] as z.infer<T>), schema]);
}

// ---------------------------------------------------------------- advertiser profiles

export const fetchMyAdvertisers = () => apiFetch(`${API_BASE}/me/advertisers`, { schema: MyAdvertisersResponse });

export const postAdvertiser = (body: AdvertiserProfileRequest) =>
  apiFetch(`${API_BASE}/advertisers`, {
    method: "POST",
    body: AdvertiserProfileRequest.parse(body),
    schema: wrappedOrBare(AdvertiserProfile, "advertiser"),
  });

export const patchAdvertiser = (advertiserId: string, body: AdvertiserProfileRequest) =>
  apiFetch(`${API_BASE}/advertisers/${enc(advertiserId)}`, {
    method: "PATCH",
    body: AdvertiserProfileRequest.parse(body),
    schema: wrappedOrBare(AdvertiserProfile, "advertiser"),
  });

// ---------------------------------------------------------------- uploads (quarantine -> processing -> review)

export const postUploadIntent = (body: UploadIntentRequest) =>
  apiFetch(`${API_BASE}/uploads/intents`, { method: "POST", body: UploadIntentRequest.parse(body), schema: UploadIntentResponse });

export const postUploadFinalize = (uploadId: string) =>
  apiFetch(`${API_BASE}/uploads/${enc(uploadId)}/finalize`, { method: "POST", body: {}, schema: UploadView });

export const fetchUpload = (uploadId: string, signal?: AbortSignal) => apiFetch(`${API_BASE}/uploads/${enc(uploadId)}`, { schema: UploadView, signal });

/**
 * The only place raw bytes are sent: `PUT` to the same-origin, session-bound path the intent
 * returned. Anything that is not `/api/v1/uploads/<id>/content` on this origin is refused, so a
 * tampered response can never make the browser send the file elsewhere.
 */
export function isSafeUploadUrl(uploadUrl: string, uploadId: string): boolean {
  return uploadUrl === `${API_BASE}/uploads/${enc(uploadId)}/content`;
}

export interface UploadProgress {
  loaded: number;
  total: number;
}

/**
 * Stream the file with progress (XMLHttpRequest is the browser API that reports upload progress).
 * Resolves on 2xx; rejects with an {@link ApiError} carrying the server's code otherwise.
 */
export function putUploadContent(
  uploadUrl: string,
  file: Blob,
  contentType: string,
  onProgress: (p: UploadProgress) => void,
  signal?: AbortSignal,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", uploadUrl, true);
    xhr.withCredentials = true;
    xhr.setRequestHeader("content-type", contentType);
    xhr.setRequestHeader("accept", "application/json");
    xhr.upload.onprogress = (e) => onProgress({ loaded: e.loaded, total: e.lengthComputable ? e.total : file.size });
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress({ loaded: file.size, total: file.size });
        resolve();
        return;
      }
      reject(xhrError(xhr));
    };
    xhr.onerror = () => reject(new ApiError("DEPENDENCY_UNAVAILABLE", "The upload was interrupted. Check your connection and try again.", { status: 0, retryable: true }));
    xhr.onabort = () => reject(new DOMException("Upload canceled", "AbortError"));
    signal?.addEventListener("abort", () => xhr.abort(), { once: true });
    xhr.send(file);
  });
}

function xhrError(xhr: XMLHttpRequest): ApiError {
  try {
    const body = JSON.parse(xhr.responseText) as { error?: { code?: string; message?: string; retryable?: boolean } };
    if (body?.error?.code && body.error.message) {
      return new ApiError(body.error.code as ApiError["code"], body.error.message, { status: xhr.status, retryable: Boolean(body.error.retryable) });
    }
  } catch {
    /* not JSON (e.g. a proxy's 413 page) */
  }
  if (xhr.status === 413) return new ApiError("UPLOAD_REJECTED", "The file is larger than the upload limit.", { status: 413 });
  if (xhr.status === 401) return new ApiError("UNAUTHENTICATED", "Sign in again to upload.", { status: 401 });
  if (xhr.status === 429) return new ApiError("RATE_LIMITED", "Too many uploads. Wait a moment, then try again.", { status: 429, retryable: true });
  return new ApiError(xhr.status >= 500 ? "INTERNAL" : "VALIDATION_FAILED", `The upload failed (${xhr.status}).`, {
    status: xhr.status,
    retryable: xhr.status >= 500,
  });
}

// ---------------------------------------------------------------- creatives

export const postCreative = (body: CreativeSubmitRequest) =>
  apiFetch(`${API_BASE}/creatives`, { method: "POST", body: CreativeSubmitRequest.parse(body), schema: wrappedOrBare(CreativeVersionView, "creative") });

export const fetchMyCreatives = () => apiFetch(`${API_BASE}/me/creatives`, { schema: MyCreativesResponse });

/** The CURRENT owner shows an APPROVED storefront creative on a store (A25: anyone else is refused). */
export const putPlacement = (propertyId: string, body: PlacementUpdateRequest) =>
  apiFetch(`${API_BASE}/properties/${enc(propertyId)}/placement`, {
    method: "PUT",
    body: PlacementUpdateRequest.parse(body),
    schema: PlacementUpdateResponse,
  });

/**
 * The stores this account currently owns: the commercial list read with the visitor's session marks
 * the current owner's stores with `edit_interior` (a current-owner-only action). Read past the HTTP
 * cache, because ownership can change at any time (A25). The placement route re-checks ownership.
 */
export async function fetchMyStores(): Promise<PropertyPublic[]> {
  const res = await apiFetch(`${API_BASE}/properties?type=COMMERCIAL`, { schema: PropertyListResponse, cache: "no-cache" });
  return res.items.filter(isMyStore);
}

export function isMyStore(p: Pick<PropertyPublic, "type" | "permittedActions">): boolean {
  return p.type === "COMMERCIAL" && p.permittedActions.includes("edit_interior");
}

// ---------------------------------------------------------------- review (moderator / admin; enforced by the server)

/**
 * `state`: "pending" (oldest first, the default queue, open urgent flags first), "approved" (newest first; where
 * revocations start) or "rejected" (newest first; decision history incl. the automated review's rejections).
 */
export type ReviewQueueState = "pending" | "approved" | "rejected";
export const fetchReviewQueue = (cursor?: string | null, state: ReviewQueueState = "pending") =>
  apiFetch(`${API_BASE}/review/creatives?state=${state}${cursor ? `&cursor=${enc(cursor)}` : ""}`, { schema: ReviewQueueResponse });

/** Audited decision; the answer is not needed (the queue is re-read afterwards). */
export const postReviewDecision = (creativeId: string, body: ReviewDecisionRequest) =>
  apiFetch<unknown>(`${API_BASE}/review/creatives/${enc(creativeId)}/decision`, { method: "POST", body: ReviewDecisionRequest.parse(body) });

/** ADR-0017: close the automated review's urgent flag (audited; note required). */
export const postAiFlagResolution = (creativeId: string, body: AiFlagResolveRequest) =>
  apiFetch<unknown>(`${API_BASE}/review/creatives/${enc(creativeId)}/ai-flag`, { method: "POST", body: AiFlagResolveRequest.parse(body) });

// ---------------------------------------------------------------- billboards

export const fetchBillboards = () => apiFetch(`${API_BASE}/billboards`, { schema: BillboardListResponse, cache: "no-cache" });

export const fetchBillboardAvailability = (billboardId: string, from: string, to: string) =>
  apiFetch(`${API_BASE}/billboards/${enc(billboardId)}/availability?from=${enc(from)}&to=${enc(to)}`, { schema: BillboardAvailabilityResponse });

// ---------------------------------------------------------------- statistics

export const fetchAdvertiserStats = () => apiFetch(`${API_BASE}/me/advertiser-stats`, { schema: AdvertiserStatsResponse });

export const ADVERTISER_STATS_EXPORT_PATH = `${API_BASE}/me/advertiser-stats/export`;

/** Filename from `Content-Disposition`, restricted to safe characters; otherwise a dated default. */
export function exportFilename(contentDisposition: string | null, today: string): string {
  const m = contentDisposition ? /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(contentDisposition) : null;
  const raw = m?.[1] ? decodeURIComponentSafe(m[1]) : "";
  const safe = raw.replace(/[^A-Za-z0-9._-]/g, "");
  return safe && safe.toLowerCase().endsWith(".csv") ? safe : `townsy-advertiser-stats-${today}.csv`;
}

function decodeURIComponentSafe(s: string): string {
  try {
    return decodeURIComponent(s);
  } catch {
    return s;
  }
}

/**
 * Download the account's own statistics as CSV (the server escapes spreadsheet formula prefixes).
 * Fetched first so a sign-in problem shows as a message instead of a JSON page.
 */
export async function downloadAdvertiserStatsCsv(today: string): Promise<void> {
  let res: Response;
  try {
    res = await fetch(ADVERTISER_STATS_EXPORT_PATH, { credentials: "include", headers: { accept: "text/csv" } });
  } catch {
    throw new ApiError("DEPENDENCY_UNAVAILABLE", "Can't reach Townsy right now. Check your connection.", { status: 0, retryable: true });
  }
  if (!res.ok) {
    let message = `The export failed (${res.status}).`;
    let code: ApiError["code"] = res.status === 401 ? "UNAUTHENTICATED" : res.status === 429 ? "RATE_LIMITED" : res.status >= 500 ? "INTERNAL" : "VALIDATION_FAILED";
    try {
      const body = (await res.json()) as { error?: { code?: string; message?: string } };
      if (body.error?.message) message = body.error.message;
      if (body.error?.code) code = body.error.code as ApiError["code"];
    } catch {
      /* not JSON */
    }
    throw new ApiError(code, message, { status: res.status, retryable: res.status === 429 || res.status >= 500 });
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement("a");
    a.href = url;
    a.download = exportFilename(res.headers.get("content-disposition"), today);
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  }
}
